<template>
    <div class="col-sm-12 nopads">
        <div class="d-flex flex-column editor-form" style="height: 85vh; border-radius: .5em">
            <header class="flex-shrink-0 border-bottom">
                <div v-if="showTitle" class="col-sm-12 nopads pt-2">
                    <h2 class="editor-header">{{ orderUpdate?.id > 0 ? $t('orders.edit') : $t('orders.add')}}</h2>
                </div>
            </header>

            <main class="flex-grow-1 overflow-auto px-1">
                <!-- OrderWizard -->
                <order-wizard v-if="!orderUpdate.contract && !(loading > 0)" :user="user"
                    :initial-contract-id="order.contract != null ? order.contract.id : undefined"
                    @contractSet="setContract" />
                <div v-if="orderUpdate.contract" class="col-sm-12"  style="overflow-x: hidden">
                    <div class="col-12 nopads">
                        <div v-if="orderUpdate.id > 0" class="col-sm-6 nopads">
                            <span class="span-title">
                                {{ $t('orders.id') }}
                            </span>
                            <span v-if="orderUpdate.id" class="span-text">
                                <strong>
                                    {{ orderUpdate.id }}
                                </strong>
                            </span>
                            <span v-else>
                                -
                            </span>
                        </div>
                        <div v-if="orderUpdate.id < 1" class="nopads col-12">
                            <span class="span-title">
                                {{ $t('orders.contract') }}
                            </span>
                            <span v-if="orderUpdate.contract" class="span-text">
                                <strong>
                                    {{ orderUpdate.contract_name }}
                                </strong>
                            </span>
                            <span v-else>
                                -
                            </span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-6 nopads">
                            <span class="span-title">
                                {{ $t('orders.customer') }}
                            </span>
                            <span v-if="orderUpdate.customer_name" class="span-text">
                                <strong>
                                    {{ orderUpdate.customer_name }}
                                </strong>
                            </span>
                            <span v-else>
                                -
                            </span>
                        </div>
                        <div class="col-sm-6 nopads">
                            <span class="span-title">
                                {{ $t('orders.contractor') }}
                            </span>
                            <span v-if="orderUpdate.contractor_name" class="span-text">
                                <strong>
                                    {{ orderUpdate.contractor_name }}
                                </strong>
                            </span>
                            <span v-else>
                                -
                            </span>
                        </div>
                    </div>
                    <div v-if="orderUpdate.id < 1" class="col-sm-12 nopads">
                        <!-- Task type selection - only in add mode -->
                        <span class="span-title">
                            {{ $t('orders.task_type') }}
                        </span>
                        <select
                            id="tasks"
                            v-model="orderUpdate.contract_task_type.id"
                            class="form-select form-select-sm mb-3"
                            :disabled="orderUpdate.contract === undefined"
                            @change="prefillUnit"
                        >
                            <option :value="null" disabled>
                                {{ $t('orders.select_task_type') }}
                            </option>
                            <option
                                v-for="option in contractTaskTypes"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ option.task_type.name }}, {{ option.amount }} {{ getUnitText(option.unit) }}
                            </option>
                        </select>
                    </div>
                    <div v-else class="row">
                        <div class="col-sm-6 nopads">
                            <span class="span-title">
                                {{ $t('orders.contract') }}
                            </span>
                            <span class="span-text">
                                <strong>
                                    {{ orderUpdate.contract_name }}
                                </strong>
                            </span>
                        </div>
                        <div class="col-sm-6 nopads">
                            <span class="span-title">
                                {{ $t('orders.task_type') }}
                            </span>
                            <span class="span-text">
                                <strong>
                                    {{ orderUpdate.contract_task_type_name }}, {{
                                        orderUpdate.ordered_amount
                                    }} {{ getUnitText(orderUpdate.unit) }}
                                </strong>
                            </span>
                        </div>
                    </div>


                    <!-- Edit fields open in both add and edit mode -->
                    <hr />
                    <h4 class="form-header">{{ $t('orders.basic_info') }}</h4>
                    <span class="tip-light"> {{ $t('orders.state_tip') }}</span>
                    <div class="row nopads pb-2">
                        <div class="col-sm-3 nopads pr-1">
                            <span class="span-title">{{ $t('orders.status') }}</span>
                            <select
                                id="taskState"
                                v-model="orderUpdate.status"
                                class="form-select form-select-sm"
                            >
                                <option :value="3">
                                    {{ $t('orders.status_plan') }}
                                </option>
                                <option :value="4">
                                    {{ $t('orders.status_stored') }}
                                </option>
                                <option :value="1">
                                    {{ $t('orders.status_open') }}
                                </option>
                                <option :value="2">
                                    {{ $t('orders.status_in_progress') }}
                                </option>
                                <option :value="10">
                                    {{ $t('orders.status_completed') }}
                                </option>
                                <option :value="20">
                                    {{ $t('orders.status_cancelled') }}
                                </option>
                                <option :value="30">
                                    {{ $t('orders.status_approved') }}
                                </option>
                                <option :value="40">
                                    {{ $t('orders.status_invoiced') }}
                                </option>
                                <option :value="closed">
                                    {{ $t('orders.status_closed') }}
                                </option>
                                <option :value="aborted">
                                    {{ $t('orders.status_aborted') }}
                                </option>
                            </select>
                        </div>
                        <div class="col-sm-3 nopads pr-1">
                            <span class="span-title">
                                {{ $t('orders.object') }}
                            </span>
                            <input
                                id="objectNumberAmount"
                                v-model.number="orderUpdate.object_number"
                                type="text"
                                class="form-control form-control-sm"
                            />
                        </div>
                        <div class="col-sm-3 nopads pr-1">
                            <span class="span-title">
                                {{ $t('orders.responsible') }}
                            </span>
                            <select
                                id="respPerson"
                                v-model="orderUpdate.resp_person.id"
                                class="form-select form-select-sm"
                            >
                                <option :value="null" disabled>
                                    {{ $t('orders.select_person') }}
                                </option>
                                <option
                                    v-for="option in persons"
                                    :key="option.id"
                                    :value="option.id"
                                >
                                    {{ option.last_name }}, {{ option.first_name }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="row nopads pb-2"
                        v-if="orderUpdate && (orderUpdate.status === closed || orderUpdate.status === aborted)">
                        <div class="col-sm-4 nopads pr-1">
                            <span class="span-title">{{ $t(orderUpdate.status === closed ? 'orders.close_time' :
                                'orders.abort_time')
                                }}</span>
                            <date-time-picker class="routa-input" v-model:date="orderUpdate.complete_time"
                                format="dd.MM.yyyy HH:mm" :enableTimePicker="true"
                                :placeholder="$t('observations_videos.search_input_format')" />
                        </div>
                    </div>
                    <div class="col-12">
                        <span class="span-title">{{ $t('orders.info') }}</span>
                        <textarea
                            id="info"
                            v-model="orderUpdate.info"
                            :disabled="!(contract?.is_contract_party)"
                            class="form-control"
                            rows="3"
                        />
                    </div>
                    <h4 class="form-header">{{ $t('orders.executor') }}</h4>
                    <span class="tip-light"> {{ $t('orders.executor_tip') }}</span>
                    <div v-if="orderUpdate.executors.length > 0 && contract?.is_contract_party">
                        <div class="col-sm-12 inspectable-container">
                            <div v-for="item in orderUpdate.executors" :key="item.id" :class="{ 'd-inline': true }">
                                <span class="inspectables inspectables-selected"
                                    @click="updateExecutorSelection(item.company)">
                                    {{ item.company.name }}
                                    <strong>
                                        X
                                    </strong>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="contract && !contract.is_contract_party">
                        <div class="col-sm-12 inspectable-container">
                            <div v-for="item in orderUpdate.executors" :key="item.id" :class="{ 'd-inline': true }">
                                <span class="inspectables inspectables-selected">
                                    {{ item.company.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="col-sm-12 small-pad">
                        <span>
                            {{ $t('orders.no_executors') }}
                        </span>
                    </div>
                    <span v-if="orderUpdate.executors.length !== executors.length" class="span-title pt-2">
                        {{ $t('orders.add_executor') }}
                    </span>
                    <div class="col-12 inspectable-container nopads pb-3 my-2">
                        <div v-for="item in executors" :key="item.id"
                            class="col-sm-6 col-md-4 col-lg-3 col-xl-2 inline inspectables nopads"
                            :class="{ 'inspectables-disabled': isSelected(item.id) }"
                            @click="updateExecutorSelection(item)">
                            {{ item.name }}
                        </div>
                    </div>

                    <!-- Plan section-->
                    <hr class="mb-1 mt-1" />

                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="planVisible = !planVisible">{{ $t('orders.plan') }}</h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="planVisible = !planVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: planVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse routa-collapse--overflow-visible" :class="{ collapsed: planVisible }">
                        <div class="row">
                            <div class="col-sm-6">
                                <span class="span-title">
                                    {{ $t('orders.planned_start') }}
                                </span>
                                <input
                                    id="start"
                                    type="date"
                                    :value="handleDateInput(orderUpdate.planned_start)"
                                    class="form-control form-control-sm"
                                    @input="updateStartDate"
                                />
                            </div>
                            <div class="col-sm-6">
                                <span class="span-title">
                                    {{ $t('orders.planned_ready') }}
                                </span>
                                <input
                                    id="end"
                                    type="date"
                                    :value="handleDateInput(orderUpdate.planned_ready)"
                                    class="form-control form-control-sm"
                                    @input="updateEndDate"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-6">
                                <span class="span-title">
                                    {{ $t('orders.amount') }} ({{ orderUpdate.unit ? getUnitText(orderUpdate.unit) : '-' }})
                                </span>
                                <input
                                    id="orderedAmount"
                                    v-model.number="orderUpdate.ordered_amount"
                                    type="number"
                                    class="form-control form-control-sm"
                                />
                            </div>
                            <!-- Realized amount not relevant when adding new -->
                            <div class="col-sm-6">
                                <span class="span-title">
                                    {{ $t('orders.realized_amount') }} ({{ orderUpdate.unit ? getUnitText(orderUpdate.unit)
                                    : '-' }})
                                </span>
                                <input
                                    id="realizedAmount"
                                    v-model.number="orderUpdate.realized_amount"
                                    type="number"
                                    class="form-control form-control-sm"
                                />
                            </div>
                        </div>
                        <div class="row pe-1">
                            <order-vehicle-allocation
                                v-model="orderUpdate.allocated_vehicles"
                                :order="order"
                                @vehiclesChanged="changedVehicles"
                            />
                        </div>
                        <div class="col-12 nopads pb-3">
                            <span class="span-title">{{ $t('orders.plan_info') }}</span>
                            <textarea
                                id="plan"
                                v-model="orderUpdate.plan"
                                class="form-control"
                                rows="3"
                            />
                        </div>
                    </div>

                    <!-- ELY Section -->
                    <div v-if="contract && contract.harja_contract_id" class="nopads">
                        <hr class="mb-1 mt-1" />
                        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                            <div class="flex-grow-1">
                                <h4 class="form-header pointer hover-header" @click.stop="elyVisible = !elyVisible">{{ $t('orders.external_section') }}</h4>
                            </div>
                            <div class="ms-2 px-2" @click.stop="elyVisible = !elyVisible">
                                <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: elyVisible }"/>
                            </div>
                        </div>
                        <div class="routa-collapse" :class="{ collapsed: elyVisible }">
                            <div class="row col-12 nopads pb-4">
                                <!-- External upload switch -->
                                <div class="col-sm-6 nopads">
                                    <span class="span-title">{{ $t('orders.external_upload_header') }}</span>
                                    <div class="form-check pt-1">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="externalUpload"
                                            v-model="orderUpdate.external_upload"
                                        />
                                        <label class="form-check-label" for="externalUpload">
                                            {{ $t('orders.external_upload_checkbox_text') }}
                                        </label>
                                    </div>
                                    <span class="tip-light">{{ $t('orders.external_upload_hint') }}</span>
                                </div>
                                <div class="col-sm-6 nopads pr-1">
                                    <span class="span-title">
                                        {{ $t('orders.external_task_id') }}
                                    </span>
                                    <div class="nopads" v-if="orderUpdate.external_tasks !== null">
                                        <div v-for="(eTask, index) in orderUpdate.external_tasks" v-bind:key="eTask.id"
                                            style="border-bottom: #d9d9d9 1px solid" class="row col-12 nopads pt-1 pb-1">
                                            <div class="col-10 nopads">
                                                <span class="nopads col-10 pr-2" style="font-size: .9em">
                                                    {{ eTask.external_task_id }} - {{ eTask.external_task_descr }}
                                                </span>
                                            </div>
                                            <div class="col-2 nopads text-right">
                                                <button :id="'removeGeometry' + eTask.id"  class="btn btn-sm btn-danger pt-1 pb-1 mx-1" @click.stop="removeExtTask(index)">
                                                    <i class="fa fa-trash" style="font-size: .8em; padding: 0; line-height: 1em;" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="row col-12 nopads mb-1">
                                        <span class="col-10">-</span>
                                    </div>
                                    <div class="row col-12 nopads mt-2">
                                        <span class="col-9 nopads pr-2 tip-light">{{
                                            $t('orders.external_task_hint')}}</span>
                                        <div class="col-3 text-right pt-2 nopads">
                                            <button class="btn btn-sm btn-info" @click.stop="fetchExtTasks">
                                                {{ $t('common.add') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="row col-12 nopads pb-4">
                                <!-- External upload switch -->
                                <div class="col-sm-6 nopads">
                                    <span class="span-title">{{ $t('orders.external_inspection_header') }}</span>
                                    <div class="col-12 nopads">
                                        <div class="form-check pt-1">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="generateExternalReports"
                                                v-model="orderUpdate.generate_external_reports"
                                            />
                                            <label class="form-check-label" for="generateExternalReports">
                                                {{ $t('orders.external_generate_report_checkbox_text') }}
                                            </label>
                                        </div>
                                    </div>
                                    <span class="tip-light">{{ $t('orders.external_inspection_hint') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Order routes -->
                    <hr class="mb-1 mt-1 hidden" />
                    <div class="row col-12 hidden nopads pb-2">
                        <div class="col-8 nopads">
                            <h4 class="form-header">{{ $t('orders.routes') }}</h4>
                        </div>
                        <div class="col nopads pt-2">
                            <div class="nopads text-end" v-if="!geomLoading">
                                <button v-if="geometrySet" class="btn btn-info btn-sm form-button" :disabled="loading > 0" @click.stop="navigateToRouteOptimization">
                                    {{ $t('orders.optimize_routes') }}
                                </button>
                            </div>
                            <div class="button-container" style="font-size: .9em; color: #BCBCBC" v-else>
                                {{ $t('common.loading') }}
                            </div>
                        </div>
                    </div>

                    <!-- Order geometry -->
                    <hr class="mb-1 mt-1" />
                    <div class="row col-12 nopads">
                        <div class="col-8 nopads">
                            <h4 class="form-header">{{ $t('orders.geometry') }}</h4>
                            <span class="tip-light">{{ $t('orders.geometry_hint') }}</span>
                        </div>
                    </div>
                    <div class="button-container">
                        <button v-if="orderUpdate.id > 0" class="btn btn-outline-info btn-sm form-button" @click.stop="showCompletionMap">
                            {{ $t('orders.show_completion_map') }}
                        </button>
                        <button v-if="geometrySet && contract?.is_contract_party" class="btn btn-sm btn-danger form-button" :disabled="loading > 0" @click.stop="removeOrderGeometry">
                            {{ $t('orders.remove_geometry') }}
                        </button>
                        <button v-if="geometrySet" class="btn btn-primary btn-sm form-button" :disabled="loading > 0" @click.stop="showOrderGeometry">
                            {{ $t('orders.show_geometry') }}
                        </button>
                        <button v-if="!geometrySet" class="btn btn-success btn-sm form-button" :disabled="loading > 0" @click.stop="addOrderGeometry">
                            {{ $t('orders.add_geometry') }}
                        </button>
                    </div>
                    <!-- Optimized Order Routes -->
                    <div v-if="orderRoutes.length > 0" class="row col-12 nopads pb-2">
                        <hr class="mb-1 mt-1" />
                        <div class="col-8 nopads">
                            <h4 class="form-header">{{ $t('orders.routes') }}</h4>
                            <span class="tip-light">{{ $t('orders.routes_hint') }}</span>
                        </div>
                        <div class="col nopads pt-4">
                            <div class="nopads text-end">
                                <button class="btn btn-outline-info btn-sm form-button" :disabled="loading > 0" @click.stop="showRouteMapModal = true">
                                    {{ $t('orders.show_routes') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Material reporting section-->
                    <hr class="mb-1 mt-1" />
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="materialsVisible = !materialsVisible">
                                {{ $t('orders.material_reporting') }} ({{ orderUpdate.material_reporting ? $t('common.in_use')
                                :
                                $t('common.inactive')}})
                            </h4>
                            <span class="tip-light">{{ $t('orders.material_reporting_hint')}}</span>
                        </div>
                        <div class="ms-2 px-2" @click.stop="materialsVisible = !materialsVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated:materialsVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse" :class="{ collapsed: materialsVisible }">
                        <div class="col-12 nopads">
                            <div class="col-sm-3 nopads pr-1">
                                <div class="form-check pt-1">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="materialReporting"
                                        v-model="orderUpdate.material_reporting"
                                    />
                                    <label class="form-check-label" for="materialReporting">
                                        {{ $t('orders.material_reporting') }}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 row nopads pb-4">
                            <div class="col-sm-6 nopads pr-1 pl-1">
                                <span class="span-title">
                                    {{ $t('orders.material_prefill') }}
                                </span>
                                <input
                                    v-model="orderUpdate.material_prefill"
                                    :placeholder="$t('orders.material_prefill_placeholder')"
                                    :disabled="!orderUpdate.material_reporting"
                                    type="text"
                                    class="form-control form-control-sm"
                                />
                            </div>
                            <div class="col-sm-3 nopads pl-1 pr-1">
                                <span class="span-title">
                                    {{ $t('orders.material_report_unit') }}
                                </span>
                                <input
                                    v-model="orderUpdate.material_report_unit"
                                    :placeholder="$t('orders.material_report_unit_placeholder')"
                                    :disabled="!orderUpdate.material_reporting"
                                    type="text"
                                    class="form-control form-control-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Cost section -->
                    <div v-if="allowPricingEditing" class="nopads">
                        <hr class="mb-1 mt-1" />
                        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                            <div class="flex-grow-1">
                                <h4 class="form-header pointer hover-header" @click.stop="costsVisible = !costsVisible">{{ $t('orders.pricing_info') }}</h4>
                                <span class="tip-light" style="color: #1a7cd2 !important;">{{ $t('orders.price_hint')}}</span>
                            </div>
                            <div class="ms-2 px-2" @click.stop="costsVisible = !costsVisible">
                                <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated:costsVisible }"/>
                            </div>
                        </div>
                        <div class="routa-collapse" :class="{ collapsed: costsVisible }">
                            <div v-if="allowPricingEditing()" class="nopads pb-2">
                                <div class="row">
                                    <div class="col-sm-4">
                                        <pricing-model-filter v-model="pricingModel" />
                                    </div>
                                    <div class="col-sm-4">
                                        <div v-if="selectPriceList" class="nopads">
                                            <span class="span-title">{{ $t('orders.pricelist') }}</span>
                                            <!-- Price list -->
                                            <select
                                                id="pricingModel"
                                                v-model="priceList"
                                                class="form-select form-select-sm"
                                            >
                                                <option :value="null">
                                                    {{ $t('orders.select_pricelist') }}
                                                </option>
                                                <!-- Loop price lists -->
                                                <option v-for="option in priceLists" :key="option.id" :value="option.id">
                                                    {{ option.name }}
                                                </option>
                                            </select>
                                            <div class="col-sm-12 d-flex justify-content-end nopads">
                                                <button class="btn btn-primary btn-sm form-button" :disabled="loading > 0" @click.stop="showPriceListEditor()">
                                                    {{ $t('orders.add_pricelist') }}
                                                </button>
                                            </div>
                                        </div>
                                        <!-- Area price list -->
                                        <div v-else-if="selectAreaPriceList" class="nopads">
                                            <span class="span-title">{{ $t('orders.pricelist') }}</span>
                                            <!-- Price list -->
                                            <select
                                                id="areaPriceList"
                                                v-model="areaPriceList"
                                                class="form-select form-select-sm"
                                            >
                                                <option :value="null">
                                                    {{ $t('orders.select_pricelist') }}
                                                </option>
                                                <!-- Loop price lists -->
                                                <option v-for="option in areaPriceLists" :key="option.id" :value="option.id">
                                                    {{ option.name }}
                                                </option>
                                            </select>
                                            <div class="col-sm-12 button-container nopads">
                                                <button class="btn btn-primary btn-sm form-button" :disabled="loading > 0"
                                                    @click.stop="showAreaPriceListEditor()">
                                                    {{ $t('orders.add_pricelist') }}
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Single price -->
                                        <div v-else class="nopads">
                                            <span class="span-title">{{ $t('orders.unit_price', { currency: currency }) }}</span>
                                            <input
                                                id="unitPriceAmount"
                                                v-model.number="orderUpdate.unit_price"
                                                type="number"
                                                step="0.001"
                                                class="form-control form-control-sm"
                                                :disabled="!pricingModel"
                                            />
                                        </div>
                                    </div>
                                    <div class="col-sm-4 text-end" style="padding-top: 1.5em">
                                        <button class="btn btn-outline-info btn-sm form-button" :disabled="loading > 0"
                                                @click.stop="showOrderPriceHistory">
                                            {{ $t('orders.show_order_price_history') }}
                                        </button>
                                    </div>
                                </div>
                                <div class="row">
                                    <div v-if="isCustomerSupervisor" class="col-sm-4">
                                        <span class="span-title">
                                            {{ $t('account.invoicing_code') }}
                                        </span>
                                        <select
                                            id="invoicing_code"
                                            v-model="orderUpdate.invoicing_code"
                                            class="form-select form-select-sm"
                                        >
                                            <option :value="null">
                                                {{ $t('tasktypes.conversion.select_code') }}
                                            </option>
                                            <option
                                                v-for="option in forestCodes"
                                                :key="option.id"
                                                :value="option"
                                            >
                                                {{ option.name }}
                                            </option>
                                        </select>
                                    </div>
                                    <div v-if="isCustomerSupervisor" class="col-sm-4">
                                        <span class="span-title">
                                            {{ $t('account.account') }}
                                        </span>
                                        <select
                                            id="account"
                                            v-model="orderUpdate.account"
                                            class="form-select form-select-sm"
                                        >
                                            <option :value="null">
                                                {{ $t('account.select_account') }}
                                            </option>
                                            <option
                                                v-for="option in accounts"
                                                :key="option.id"
                                                :value="option"
                                            >
                                                {{ option.name }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-sm-4">
                                        <span class="span-title">
                                            {{ $t('orders.cost_estimation') }}
                                        </span>
                                        <input
                                            id="costEstimation"
                                            v-model.number="orderUpdate.cost_estimation"
                                            type="number"
                                            class="form-control form-control-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Attachments-->
                    <hr class="mb-1 mt-1" />
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="attachmentsVisible = !attachmentsVisible">{{ $t('orders.attachments_and_info') }} ({{orderUpdate.attachments ? orderUpdate.attachments.length : 0}})</h4>
                            <span class="tip-light"  style="color: #337003 !important;">{{ $t('orders.attachments_hint')}}</span>
                        </div>
                        <div class="ms-2 px-2" @click.stop="attachmentsVisible = !attachmentsVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated:attachmentsVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse" :class="{ collapsed: attachmentsVisible }">
                        <div v-if="orderUpdate.attachments && orderUpdate.attachments.length" class="pb-2">
                            <span class="span-title">{{ $t('common.attachments') }}</span>
                            <div
                                v-for="item in orderUpdate.attachments"
                                :key="item.id"
                                class="row border-bottom pb-1"
                                style="font-size: .9em"
                            >
                                <div
                                    class="col-10 pointer link pl-0 pt-1 ml-0"
                                    @click="downloadAttachment(item)"
                                >
                                    {{ item.file_original_name }}
                                </div>
                                <div class="col-2">
                                    <button
                                        class="btn btn-danger btn-sm ms-3"
                                        @click.stop="removeOrderAttachment(item)"
                                    >
                                        <font-awesome-icon icon="trash" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            v-for="(file, index) in attachments"
                            :key="index"
                            class="d-flex align-items-center justify-content-between mb-3"
                        >
                            <span>{{ file?.name || file }}</span>
                            <button
                                class="btn btn-outline-danger btn-sm"
                                type="button"
                                @click="removeAttachment(index)"
                            >
                                {{ $t('common.remove') }}
                            </button>
                        </div>
                        <file-drop-zone
                            :accept="getValidAttachmentTypes()"
                            class="my-2"
                            @files-selected="files => files.forEach(f => attachments.push(f))"
                        />
                    </div>
                    <hr class="nopads mt-1" />
                    <comment-container class="pt-2 pb-2" :topic="commentTopics.TOPIC_ORDERS" :id="orderUpdate.id"
                        :user="user" />
                    <hr class="mb-1 mt-1" />
                    <div class="col-12 text-right pr-4">
                        <span v-if="orderUpdate.id > 0" class="ban">
                            {{ !geomLoading && isOrderUpdateEdited ? $t('orders.order_edited') : null }}
                        </span>
                    </div>
                </div>
            </main>
            <!-- Footer -->
            <footer class="flex-shrink-0 border-top">
                <div class="col-sm-12 button-container">
                    <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                        {{ $t('common.cancel') }}
                    </button>

                    <button v-if="orderUpdate.id > 0" :disabled="loading > 0 || geomLoading" class="btn btn-primary form-button" @click.stop="copyOrder">
                        {{ $t('common.copy') }}
                    </button>

                    <button class="btn btn-success form-button" :disabled="loading > 0 || geomLoading" @click.stop="submitOrder">
                        {{ $t('common.save') }}
                    </button>
                </div>
            </footer>
        </div>

        <!-- Geometry importer -->
        <base-modal v-if="showGeometryForm" size="xl" hide-footer hide-header prevent-close no-padding>
            <order-geometry  :geometries="orderUpdate.geometries" :contract-id="orderUpdate.contract"
                :contract="contract" :order-id="orderUpdate.id" :is-observer="isObserver" :user="user" :isSubContractor="isSubContractor"
                :addGeometriesToMapOnLoad="false" :allowLayersPostProcessing="true" @geometrySelected="setGeometry"
                @close="hideOrderGeometry" />
        </base-modal>

        <!-- Price list addition -->
        <base-modal
            v-if="priceModalVisible"
            :hide-header="true"
            :hide-footer="true"
            size="lg">
            <price-list-editor :price-list="newList" :hideTitle="true" @close="priceModalVisible = false" @closeAndUpdate="priceListAdded" />
        </base-modal>
        <base-modal
            v-if="areaPriceModalVisible"
            :prevent-close="true"
            size="lg"
            :hide-header="true"
            :hide-footer="true">
            <area-price-list-editor :price-list="newAreaPriceList()" :hideTitle="true" @close="areaPriceModalVisible = false" @closeAndUpdate="areaPriceListAdded" />
        </base-modal>

        <!-- Order price histories -->
        <order-price-history-modal v-if="showOrderPriceHistoryModal" :order-id="order.id"  @close="hideOrderPriceHistory" />

        <!-- Order completion map -->
        <order-completion-map-modal v-if="showCompletionMapModal" :show="showCompletionMapModal" :order-id="orderUpdate.id" :geometries="orderUpdate.geometries" @close="hideCompletionMap" />

        <!-- Order route map preview -->
        <order-route-map-modal v-if="showRouteMapModal" :order-routes="orderRoutes" @close="showRouteMapModal = false" />

        <!-- Order copy confirmation -->
        <base-modal
            v-if="copyConfirmation"
            :hide-header="true"
            :hide-footer="true"
            :no-top-padding="true"
            :allow-overflow="true"
            @ok="doCopyOrder">
            <div class="col-sm-12 nopads">
                <h2 class="editor-header">{{ $t('orders.copy_order') }}</h2>
                <span class="pb-2">{{ $t('orders.copy_confirm') }}</span>
            </div>
            <div class="col-12">
                <!-- Task type selection - only in add mode -->
                <span class="tip-light">
                    {{ $t('orders.contract_type_change') }}
                </span>
                <open-contract-filter :from="(new Date()).toISOString()" :contract="contract"
                    :showOnlyMyContractsOption="true" :show-title="false" :show-company="false"
                    @contractSelected="setCopyContract" />
                <!-- Task type selection - only in add mode -->
                <span class="tip-light">
                    {{ $t('orders.task_type_change') }}
                </span>
                <div class="pl-1 pr-1">
                    <select
                        id="tasks"
                        v-model="newTaskTypeObject"
                        class="form-select form-select-sm"
                        @change="prefillUnit"
                    >
                        <option :value="null" disabled>
                            {{ $t('orders.select_task_type') }}
                        </option>
                        <option v-for="option in contractTaskTypes" :key="option.id" :value="option">
                            {{ option.task_type.name }}, {{ option.amount }} {{ getUnitText(option.unit) }}
                        </option>
                    </select>
                </div>
                <!-- Executor selection  -->
                <span class="tip-light">
                    {{ $t('orders.executors_change') }}
                </span>
                <div class="pl-1 pr-1">
                    <multiselect v-model="selectedExecutors" class="multiselect_routa"
                        :placeholder="$t('orders.select_contractor')" :deselect-label="$t('common.remove_selection')"
                        :select-label="$t('common.select')" :close-on-select="false" :multiple="true" label="name"
                        track-by="id" :searchable="true" :internal-search="true" :selected-label="$t('common.selected')"
                        :options="executors" :disabled="loading > 0">
                        <template slot="singleLabel" slot-scope="props">
                            <span class="option__desc">
                                <span class="option__title">{{ props.option.name }}</span>
                            </span>
                        </template>
                        <template slot="option" slot-scope="props">
                            <div class="option__desc">
                                <span class="option__title multiselect_detail_block">{{
                                    props.option.name
                                    }}</span>
                            </div>
                        </template>
                        <span slot="noResult">{{ $t('common.not_found') }}</span>
                        <span slot="noOptions">{{ $t('common.no_results') }}</span>
                    </multiselect>
                </div>
            </div>
            <div class="col-12 button-container">
                <button class="btn btn-sm btn-secondary form-button" @click="cancelCopying">
                    {{ $t('common.no') }}
                </button>
                <button class="btn btn-sm btn-success form-button" @click="doCopyOrder">
                    {{ $t('common.yes') }}
                </button>
            </div>
        </base-modal>

        <!-- External task selection modal -->
        <base-modal
            v-if="externalTasksModal"
            :hide-footer="true"
            :title="$t('orders.select_task')">
            <external-task-selector ref="extTaskSelector" :tasks="extTasks" />
            <div class="col-12 button-container">
                <button class="btn btn-sm btn-secondary form-button" @click="externalTasksModal = false">
                    {{ $t('common.cancel') }}
                </button>
                <button size="sm" class="btn btn-sm btn-success form-button" @click="setExtTask()">
                    {{ $t('common.save') }}
                </button>
            </div>
        </base-modal>
        <div v-if="loading > 0" id="loader" class="spinner" />
    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import { units, workManagementHelper } from '../mixins/WorkManagementMixin'
import { userHelper } from '../mixins/UserMixin'
import OrderWizard from './OrderWizard'
import { attachmentHelper } from "@/components/mixins/AttachmentMixin";
import { priceHelper } from "../mixins/PricingMixin";
import PriceListEditor from "@/components/pricingmodels/pricelists/PriceListEditor";
import PricingModelFilter from "../pricingmodels/PricingModelFilter"
import OrderGeometry from "./OrderGeometry";
import { measureDistanceMixin } from "../mixins/MeasureDistanceMixin";
import { timeUtils } from "@/components/mixins/TimeUtils";
import { commentTopics } from "@/components/mixins/CommentMixin";
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import OrderPriceHistoryModal from "@/components/order/OrderPriceHistoryModal";
import AreaPriceListEditor from "@/components/pricingmodels/pricelists/AreaPriceListEditor";
import ExternalTaskSelector from "@/components/velho/ExternalTaskSelector";
import CommentContainer from "@/components/comments/CommentContainer.vue";
import DateTimePicker from "@/components/common/DateTimePicker";
import BaseModal from "@/components/BaseModal";
import {inject} from "vue";
import {vehicleHelper} from "@/components/mixins/VehicleMixin";
import OrderVehicleAllocation from "@/components/order/OrderVehicleAllocation.vue";
import OrderCompletionMapModal from "@/components/order/OrderCompletionMapModal.vue";
import OrderRouteMapModal from "@/components/order/OrderRouteMapModal.vue";
import FileDropZone from '@/components/common/FileDropZone.vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    name: 'OrderEditor',
    components: {
        FontAwesomeIcon,
        BaseModal,
        OrderVehicleAllocation,
        OrderRouteMapModal,
        CommentContainer,
        ExternalTaskSelector,
        AreaPriceListEditor, OrderPriceHistoryModal,
        OpenContractFilter, PricingModelFilter, OrderGeometry, PriceListEditor, OrderWizard,
        DateTimePicker, OrderCompletionMapModal,
        FileDropZone
    },
    mixins: [workManagementHelper, restApi, userHelper, commentTopics,
        attachmentHelper, priceHelper, measureDistanceMixin, units, timeUtils, vehicleHelper],
    props: {
        contract: {
            type: Object,
            default: null
        },
        order: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        showTitle: {
            type: Boolean,
            default: true
        },
        isObserver: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        const setActiveView = inject('setActiveView', null);
        return { showAlert, setActiveView };
    },
    data: function () {
        return {
            loading: 0,
            showOrderInfo: false,
            showStateInfo: false,
            showExecInfo: false,
            showCostInfo: false,
            showMaterialInfo: false,
            showAttInfo: false,
            planVisible: false,
            elyVisible: false,
            geometryVisible: false,
            costsVisible: false,
            attachmentsVisible: false,
            materialsVisible: false,
            showExtTaskSelection: false,
            copyConfirmation: false,
            executors: [],
            persons: [],
            contractTaskTypes: [],
            orderUpdate: JSON.parse(JSON.stringify(this.order)),
            originalOrderUpdate: {},
            attachments: [],
            accounts: [],
            forestCodes: [],
            pricingModel: null,
            priceLists: [],
            areaPriceLists: [],
            contracts: [],
            vehiclesChanged: false,
            extTasks: [],
            contractSet: null,
            priceList: null,
            areaPriceList: null,
            newList: null,
            showGeometryForm: false,
            showOrderPriceHistoryModal: false,
            priceModalVisible: false,
            areaPriceModalVisible: false,
            externalTasksModal: false,
            geometrySet: false,
            geomModified: false,
            geomLoading: false,
            selectPriceList: false,
            selectAreaPriceList: false,
            selectedExecutors: [],
            copyEnabled: false,
            newTaskTypeObject: null,
            closed: 50,
            aborted: 60,
            showCompletionMapModal: false,
            showRouteMapModal: false,
            orderRoutes: []
        }
    },
    watch: {
        'orderUpdate.invoicing_code': function(newValue, oldValue) {
            this.orderUpdate.account = null
            if(newValue && newValue.hasOwnProperty('id')) {
                this.fetchAccountsByInvoicingCode(newValue)
            }
        },
        'orderUpdate.contract_task_type.id': function(newValue, oldValue) {
            this.orderUpdate.invoicing_code = null
            if(newValue !== null) {
                let contractTaskType = this.contractTaskTypes.filter(item => item.id === newValue)
                if(contractTaskType && contractTaskType[0]) {
                    this.fetchForestCodesByTaskType(contractTaskType[0].task_type.id)
                }

            }
        },
        pricingModel: function () {
            this.initSelectPriceList()
        },
        contractSet() {
            this.fetchContractTaskTypes(this.contractSet)
        }
    },
    computed: {
        currency() {
            return import.meta.env.VITE_APP_CURRENCY || '€'
        },
        commentTopics() {
            return commentTopics
        },
        isCustomerSupervisor() {
            return !!(this.user && this.user.company && this.user.company.id &&
                this.user.roles && this.hasObserver(this.user.roles) &&
                this.orderUpdate && this.orderUpdate.customer &&
                this.user.company.id === this.orderUpdate.customer);
        },
        isSubContractor() {
            // First, check all necessary data exists
            if (
                this.user &&
                this.user.company &&
                this.orderUpdate &&
                this.orderUpdate.customer &&
                this.orderUpdate.contractor &&
                Array.isArray(this.orderUpdate.executors) &&
                this.orderUpdate.executors.length > 0
            ) {
                const userCompanyId = this.user.company.id;

                // Check that user company is not customer or contractor
                const notCustomerOrContractor = (
                    userCompanyId !== this.orderUpdate.customer &&
                    userCompanyId !== this.orderUpdate.contractor
                );

                // Check if user company is among the executors
                const isExecutor = this.orderUpdate.executors.some(
                    executor => executor.company && executor.company.id === userCompanyId
                );

                return notCustomerOrContractor && isExecutor;
            }
            // If any data is missing
            return false;
        },
        isOrderUpdateEdited() {
            return JSON.stringify(this.originalOrderUpdate) !== JSON.stringify(this.orderUpdate)
                || this.orderUpdate.pricing_model && (!this.pricingModel || this.pricingModel.id !== this.orderUpdate.pricing_model.id)
                || !this.orderUpdate.pricing_model && this.pricingModel && this.pricingModel.id
                || !this.orderUpdate.price_list && this.priceList && this.priceList.id
                || this.orderUpdate.price_list && this.priceList !== this.orderUpdate.price_list.id
                || !this.orderUpdate.area_price_list && this.areaPriceList && this.priceList.id
                || this.orderUpdate.area_price_list && this.areaPriceList !== this.orderUpdate.area_price_list.id
                || this.geomModified || this.vehiclesChanged
        },
    },
    created: function () {
        if (this.orderUpdate.id > 0) {
            this.fetchPersons()
            this.fetchOrderGeometry()
            this.fetchOrderRoutes()
        }
        if (this.contract) {
            this.setContract(this.contract, true)
            if (this.contract.harja_contract_id && this.orderUpdate.id < 0) {
                this.orderUpdate.external_upload = true
            }
        } else if (this.orderUpdate.contract) {
            // set person fetching when new order is being added
            // this way we prevent unnecessary fetching
            if (this.orderUpdate.id < 0) {
                this.fetchPersons()
            }
            this.fetchExecutors()
            this.fetchContractTaskTypes()
        } else {
            this.fetchExecutors()
        }
        if (this.orderUpdate.pricing_model) {
            this.pricingModel = this.orderUpdate.pricing_model
        }
        if (this.orderUpdate.price_list) {
            this.priceList = this.orderUpdate.price_list.id
        }
        if (this.orderUpdate.area_price_list) {
            this.areaPriceList = this.orderUpdate.area_price_list.id
        }
        if (!this.orderUpdate.complete_time) {
            this.orderUpdate.complete_time = this.getToday()
        }
        if(this.isCustomerSupervisor && this.orderUpdate.contract_task_type.task_type && this.orderUpdate.contract_task_type.task_type.id) {
            if(this.orderUpdate.invoicing_code && this.orderUpdate.invoicing_code.id) {
                this.fetchForestCodesByTaskType(this.orderUpdate.contract_task_type.task_type.id)
                this.fetchAccountsByInvoicingCode(this.orderUpdate.invoicing_code)
            } else {
                this.fetchForestCodesByTaskType(this.orderUpdate.contract_task_type.task_type.id)
            }
        }

        this.geometrySet = this.orderUpdate.geometries && this.orderUpdate.geometries.length > 0
        this.fetchPriceLists()
        this.fetchAreaPriceLists()
        this.newList = this.newPriceList()
    },

    methods: {
        triggerFileInput() {
            this.$refs.fileInput?.click();
        },
        handleFileChange(event) {
            Array.from(event.target.files).forEach(file => this.attachments.push(file));
            event.target.value = '';
        },
        fetchPriceLists: function () {
            this.loading++;
            this.restFetchParams(this.priceListUrl, {active: 1}, this.handlePriceListResponse)
        },
        fetchAreaPriceLists: function () {
            this.loading++;
            this.restFetchParams(this.areaPriceListUrl, {active: 1}, this.handleAreaPriceListResponse)
        },
        fetchContracts() {
            this.loading++;
            this.contracts = []
            let today = new Date()
            let params = {
                startDate: this.localTimeToUtcTime(today).toISOString(),
                endDate: this.getEndOfTheDay(today).toISOString()
            }
            this.restFetchParams(this.contractOpenUrl, params, this.handleContracts)
        },
        handleContracts(response) {
            this.loading--
            if (response != null) {
                this.contracts = response.data
            }
        },
        fetchForestCodesByTaskType: function (taskType) {
            this.loading++
            this.forestCodes = []
            this.restFetchParams(this.taskTypeConversionForestCodeUrl, {task_type: taskType}, this.handleForestCodes, this.handleForestCodesFail)
        },
        handleForestCodes: function (response) {
            this.loading--
            if(response && response.data && response.data.length === 1) {
                this.forestCodes = response.data
                // set only option as selected
                if(this.orderUpdate.invoicing_code || this.orderUpdate.invoicing_code.id) {
                    this.orderUpdate.invoicing_code = response.data[0]
                }
            } else {
                this.forestCodes = response.data
            }
        },
        handleForestCodesFail() {
            this.loading--
        },
        fetchAccountsByInvoicingCode: function (invoicingCode) {
            this.loading++;
            this.accounts = []
            this.restFetchParams(this.accountConversionUrl + "/account", {forest_code: invoicingCode.id, contract_class: this.contract && this.contract.contract_class ? this.contract.contract_class.id : null}, this.handleAccounts, this.handleAccountsFail)
        },
        handleAccounts(response) {
            this.loading--
            if(response && response.data) {
                this.accounts = response.data
                if(response.data.length === 1) {
                    // set only option as selected
                    if(!this.orderUpdate.account || this.orderUpdate.account.id) {
                        this.orderUpdate.account = response.data[0]
                    }
                }
            }
        },
        handleAccountsFail() {
            this.loading--
        },
        handleDateInput(timestamp) {
            var date = new Date(timestamp);
            if (isNaN(date.getTime())) {
                return '';
            }
            return this.timestampToDateInput(timestamp);
        },
        initSelectPriceList() {
            if (this.pricingModel && this.pricingModel.unit1 && this.pricingModel.unit2 &&
                ((parseInt(this.pricingModel.unit1) === 5 || parseInt(this.pricingModel.unit1) === 6) &&
                    parseInt(this.pricingModel.unit2) === 103)) {
                this.selectPriceList = true
                this.selectAreaPriceList = false
            } else if (this.pricingModel && this.pricingModel.unit1 && this.pricingModel.unit2 &&
                parseInt(this.pricingModel.unit1) === 3 && parseInt(this.pricingModel.unit2) === 104) {
                this.selectAreaPriceList = true
                this.selectPriceList = false
            } else {
                this.selectPriceList = false
                this.selectAreaPriceList = false
            }
        },
        handlePriceListResponse: function (response) {
            this.loading--
            this.priceLists = response.data
        },
        handleAreaPriceListResponse: function (response) {
            this.loading--
            if (response && response.data) {
                this.areaPriceLists = response.data
            } else {
                this.areaPriceLists = []
            }
        },
        showPriceListEditor: function () {
            this.priceModalVisible = true
        },
        showAreaPriceListEditor: function () {
            this.areaPriceModalVisible = true
        },
        priceListAdded: function (newPriceList) {
            this.priceList = newPriceList.id
            this.fetchPriceLists()
            this.newList = this.newPriceList()
            this.priceModalVisible = false
        },
        areaPriceListAdded: function (newPriceList) {
            this.areaPriceList = newPriceList.id
            this.fetchAreaPriceLists()
            this.areaPriceModalVisible = false
        },
        showOrderPriceHistory() {
            this.showOrderPriceHistoryModal = true
        },
        hideOrderPriceHistory() {
            this.showOrderPriceHistoryModal = false
        },
        setContract: function (contract, excludeTaskTypeReset) {
            this.orderUpdate.contract = contract.id
            this.orderUpdate.contract_name = contract.name
            this.orderUpdate.customer = contract.customer.id
            this.orderUpdate.contractor = contract.contractor.id
            this.orderUpdate.customer_name = contract.customer.name
            this.orderUpdate.contractor_name = contract.contractor.name
            if (!excludeTaskTypeReset) {
                this.orderUpdate.contract_task_type = { id: null }
            }
            this.fetchContractTaskTypes()
            this.fetchExecutors()
            this.fetchPersons()
        },

        setCopyContract: function (contract) {
            this.contractSet = contract ? contract.id : null
        },

        fetchContractTaskTypes: function (contractId) {
            this.loading++
            this.$nextTick(() => {
                this.restFetchParams(this.contractTaskTypeUrl, { contract: contractId ? contractId : this.orderUpdate.contract }, this.handleContractTaskTypes)
            })
        },

        handleContractTaskTypes: function (response) {
            this.contractTaskTypes = response.data
            this.setContractTaskType()
            this.loading--
        },

        setContractTaskType() {
            if (this.contractTaskTypes.length > 0 && this.newTaskTypeObject) {
                let found = this.contractTaskTypes.find(item => item.task_type.id === this.newTaskTypeObject.task_type.id)
                if (found) {
                    // set same contract task type from new contract if found
                    this.newTaskTypeObject = found
                } else {
                    // set first contract task type if similar is not found in new contract
                    this.newTaskTypeObject = this.contractTaskTypes[0]
                }
            }
        },

        fetchPersons: function () {
            this.loading++
            this.restFetchParams(this.userUrl, { company: this.orderUpdate.contractor }, this.handlePersons)
        },

        handlePersons: function (response) {
            this.loading--
            this.persons = response.data
        },

        fetchOrderGeometry: function () {
            this.restFetch(this.orderUrl + '/' + this.orderUpdate.id + '/geometry', this.handleOrderGeometry)
            this.geomLoading = true
        },

        handleOrderGeometry: function (response) {
            this.geomLoading = false
            if (response && response.data) {
                this.orderUpdate.geometries = response.data.length > 0 ? response.data : null
                this.geometrySet = this.orderUpdate.geometries && this.orderUpdate.geometries.length > 0
            }
            this.originalOrderUpdate = JSON.parse(JSON.stringify(this.orderUpdate));
        },

        fetchExecutors: function () {
            this.restFetch(this.contractorUrl, this.handleExecutors)
        },

        handleExecutors: function (response) {
            this.executors = response.data
        },

        prefillUnit: function () {
            this.$nextTick(() => {
                if (this.orderUpdate.unit === '') {
                    this.orderUpdate.unit = this.getSelectedTaskTypeUnit(this.orderUpdate.contract_task_type.id)
                }
            })
        },

        getSelectedTaskTypeUnit: function (id) {
            var unit = ''
            var index = this.contractTaskTypes.findIndex(obj => obj.id === id)
            if (index >= 0) {
                unit = this.contractTaskTypes[index].unit
            }
            return unit
        },

        updateStartDate: function (event) {
            if (event.target.value) {
                this.orderUpdate.planned_start = new Date(event.target.value)
            } else {
                this.orderUpdate.planned_start = undefined
            }
        },

        updateEndDate: function (event) {
            if (event.target.value) {
                // We need to add 23 h 59 minutes to the date to cover the whole day
                var endDate = new Date(event.target.value)
                endDate.setHours(23)
                endDate.setMinutes(59)
                endDate.setSeconds(59)
                this.orderUpdate.planned_ready = endDate
            } else {
                this.orderUpdate.planned_ready = undefined
            }
        },

        setDefaultValues: function (orderUpdate) {
            let order = this.jsonToItem(orderUpdate)
            if (order.customer === null || order.customer < 1) {
                this.showAlert(this.$t('orders.invalid_name'))
                return undefined
            }
            if (order.contract === null || order.contract < 1) {
                this.showAlert(this.$t('orders.invalid_name'))
                return undefined
            }
            if (order.contract_task_type === null || order.contract_task_type < 1) {
                this.showAlert(this.$t('orders.invalid_name'))
                return undefined
            }
            if (order.status === this.closed || order.status === this.aborted) {
                if (!order.complete_time) {
                    if (order.status === this.closed) {
                        this.showAlert(this.$t('orders.please_enter', { key: this.$t('orders.close_time').toLowerCase() }))
                        return
                    }
                    if (order.status === this.aborted) {
                        this.showAlert(this.$t('orders.please_enter', { key: this.$t('orders.abort_time').toLowerCase() }))
                        return
                    }
                    return undefined
                }
            } else {
                order.complete_time = null
            }
            if (this.pricingModel) {
                if (!this.selectPriceList && order.unit_price) {
                    order.pricing_model = { id: this.pricingModel.id }
                    order.unit_price = order.unit_price * 100
                    order.price_list = undefined
                    order.area_price_list = undefined
                } else if (this.selectPriceList && this.priceList) {
                    order.pricing_model = { id: this.pricingModel.id }
                    order.unit_price = undefined
                    order.price_list = { id: this.priceList }
                    order.area_price_list = undefined
                } else if (this.selectAreaPriceList && this.areaPriceList) {
                    order.pricing_model = { id: this.pricingModel.id }
                    order.unit_price = undefined
                    order.area_price_list = { id: this.areaPriceList }
                    order.price_list = undefined
                } else {
                    this.showAlert(this.selectPriceList ? this.$t('orders.select_pricelist') : this.$t('orders.enter_unit_price'))
                    return undefined
                }
            } else {
                order.pricing_model = null
                order.unit_price = undefined
                order.price_list = undefined
            }
            this.orderUpdate.executors.forEach(function (c) {
                c.work_order = { id: this.orderUpdate.id }
            }, this)
            if (order.planned_start && (order.planned_start instanceof Date)) {
                order.planned_start = order.planned_start.toISOString()
            }
            if (order.planned_ready && (order.planned_ready instanceof Date)) {
                order.planned_ready = order.planned_ready.toISOString()
            }
            if (order.geometries) {
                order.geometries.forEach(item => {
                    item.geometry = typeof item.geometry === 'object' && item.geometry !== null ? JSON.stringify(item.geometry) : item.geometry
                })
            }
            if (order.allocated_vehicles) {
                order.allocated_vehicles = order.allocated_vehicles.map(v => this.vehicleToPayload(v))
            }
            return order
        },
        fetchOrderRoutes() {
            this.restFetch(this.orderUrl + '/' + this.orderUpdate.id + '/routes', this.fetchOrderRoutesSuccess, this.fetchOrderRoutesFail)
        },
        fetchOrderRoutesSuccess(response) {
            if (response && response.data) {
                this.orderRoutes = response.data
            }
        },
        fetchOrderRoutesFail(err) {
            console.error(err)
        },
        copyOrder: function () {
            this.fetchContracts()
            this.copyConfirmation = true
            this.newTaskTypeObject = this.orderUpdate.contract_task_type
        },

        cancelCopying() {
            this.copyConfirmation = false
            this.newTaskTypeObject = null
        },

        doCopyOrder: function () {
            this.copyConfirmation = false
            this.restAdd(this.orderUrl + '/' + this.orderUpdate.id + '/copy', {
                contractTaskType: this.newTaskTypeObject.id,
                copyExecutors: this.selectedExecutors && this.selectedExecutors.length > 0 ? this.selectedExecutors : null
            },
            this.copySuccess, this.fail)
        },

        submitOrder: function () {
            var json = this.setDefaultValues(this.orderUpdate)
            if (json !== undefined) {
                this.loading++
                if (this.orderUpdate.id < 1) {
                    this.restAdd(this.orderUrl, json, this.success, this.fail)
                } else {
                    this.restUpdate(this.orderUrl, json, this.success, this.fail)
                }
            }
        },

        success: function (data) {
            this.orderUpdate = this.createEditorOrder(data)
            this.uploadAttachment()
        },

        fail: function () {
            this.loading--
            this.showAlert(this.$t('common.alert_update_failed'))
            this.copyEnabled = false
        },

        isSelected: function (contractorId) {
            return this.orderUpdate.executors.find(item => item.company.id === contractorId) !== undefined
        },

        changedVehicles() {
            this.vehiclesChanged = true
        },

        updateExecutorSelection: function (company) {
            var index = this.orderUpdate.executors.findIndex(item => item.company.id === company.id)
            if (index >= 0) {
                this.orderUpdate.executors.splice(index, 1)
            } else {
                this.orderUpdate.executors.push({ company: company })
            }
        },

        uploadAttachment: function () {
            let attachments = this.getValidAttachments(this.attachments)
            if (attachments.length > 0) {
                let formData = new FormData()
                attachments.forEach(file => {
                    if (file !== undefined && file !== null) {
                        formData.append('attachments[]', file)
                    }
                })
                this.restPostWithFile(this.orderAttachmentUrl + this.orderUpdate.id, {}, formData,
                    () => {
                        this.loading--
                        this.$emit('closeAndUpdate')
                    }, this.fail
                )
            } else {
                this.loading--
                this.$emit('closeAndUpdate')
            }
        },

        copySuccess: function (data) {
            this.orderUpdate = this.createEditorOrder(data)
            this.copyEnabled = false
            this.fetchOrderGeometry()
            this.loading--
            this.showAlert(this.$t('orders.copy_successful'), 'success')
        },

        downloadAttachment: function (item) {
            this.loading++
            this.restFetch(this.orderAttachmentUrl + this.orderUpdate.id + '/' + item.id,
                response => {
                    this.saveFile(item.file_original_name, response.data)
                    this.loading--
                }, () => {
                    this.loading--
                })
        },

        removeAttachment: function (item) {
            if (this.attachments && this.attachments.length > 0) {
                var index = this.attachments.findIndex(attachment => attachment.id === item.id)
                if (index >= 0) {
                    this.attachments.splice(index, 1)
                }
            }
        },


        removeOrderAttachment: function (item) {
            if (this.orderUpdate.attachments && this.orderUpdate.attachments.length > 0) {
                var index = this.orderUpdate.attachments.findIndex(attachment => attachment.id === item.id)
                if (index >= 0) {
                    this.orderUpdate.attachments.splice(index, 1)
                }
            }
        },

        allowPricingEditing: function () {
            // Allowed only for contract's customer or contractor
            return this.orderUpdate.contractor === this.user.company.id ||
                this.orderUpdate.customer === this.user.company.id
        },

        showOrderGeometry: function () {
            this.showGeometryForm = true
        },

        removeOrderGeometry: function () {
            this.orderUpdate.geometries = []
            this.geometrySet = false
            this.geomModified = true
        },

        addOrderGeometry: function () {
            this.showGeometryForm = true
        },

        hideOrderGeometry: function () {
            this.showGeometryForm = false
        },

        setGeometry: function (geometries, geometryModified) {
            this.orderUpdate.geometries = [...geometries]
            if (geometryModified) {
                this.geomModified = geometryModified
            }
            this.geometrySet = this.orderUpdate.geometries && this.orderUpdate.geometries.length > 0
            this.hideOrderGeometry()
            if (geometries) {
                let calculatedGeometries = geometries.filter(item => item.calculate)
                if (calculatedGeometries.length > 0) {
                    this.orderUpdate.ordered_amount = this.calculateGeoJsonDataLength(calculatedGeometries)
                    this.orderUpdate.unit = units.KILOMETERS
                }
            }
        },
        fetchExtTasks() {
            this.loading++
            this.restFetchParams(this.extTasksUrl, { contract: this.orderUpdate.contract }, this.handleTasks)
        },
        handleTasks(response) {
            this.loading--
            if (response && response.data) {
                this.extTasks = response.data
                this.externalTasksModal = true
            } else {
                this.showAlert(this.$t('order.external_access_error'))
            }
        },
        setExtTask() {
            let et = this.$refs.extTaskSelector.getSelectedTask()
            if (!this.orderUpdate.external_tasks) {
                this.orderUpdate.external_tasks = []
            }
            this.orderUpdate.external_tasks.push({
                'external_task_id': et.id,
                'external_task_descr': et.selite,
                'external_type': et.type
            })
            this.externalTasksModal = false
            this.$forceUpdate()
        },
        removeExtTask(index) {
            this.orderUpdate.external_tasks.splice(index, 1)
        },
        showCompletionMap() {
            this.showCompletionMapModal = true
        },
        hideCompletionMap() {
            this.showCompletionMapModal = false
        },
        navigateToRouteOptimization() {
            if (this.setActiveView) {
                // Store contract and order info in Vuex store
                this.$store.commit('routeOptimization/setNavigationData', {
                    contractId: this.contract?.id,
                    orderId: this.orderUpdate?.id
                });
                this.setActiveView('routeOptimization', {})
            }
        }
    }
}
</script>
<style>
</style>
